import React, { useState, useEffect } from 'react';
import { X, Save, Check, Users } from 'lucide-react';

const BulkEditModal = ({
  isOpen,
  onClose,
  selectedRows,
  selectedRecord = null, // The record when exactly one row is selected
  onBulkUpdate,
  isUpdating = false,
  products = [] // Add products prop
}) => {
  const [bulkData, setBulkData] = useState({
    paymentMethod: '',
    status: '',
    productName: '',
    price: '',
    subscriptionPeriod: ''
  });

  // Email fields are only editable when a single record is selected
  const isSingleSelection = selectedRows.length === 1 && !!selectedRecord;
  const [emailData, setEmailData] = useState({ clientEmail: '', emailEdu: '' });

  // Pre-fill emails with the selected record's current values each time the modal opens
  useEffect(() => {
    if (isOpen && isSingleSelection) {
      setEmailData({
        clientEmail: selectedRecord.clientEmail || '',
        emailEdu: selectedRecord.emailEdu || ''
      });
    } else {
      setEmailData({ clientEmail: '', emailEdu: '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isSingleSelection, selectedRecord?.transactionId]);

  // Dropdown options matching the edit form
  const paymentMethods = [
    'instaPay',
    'Vfcash', 
    'Binance',
    'Gate.io',
    'Paypal',
    'Cash',
    'N/A',
    'Other'
  ];

  const statusOptions = [
    'OnHold',
    'Processing',
    'Completed',
    'Refunded',
    'Expired'
  ];

  const subscriptionPeriods = [
    '1 month',
    '3 months',
    '6 months',
    '12 months',
    '24 months',
    '36 months',
    'one-time purchase'
  ];

  const handleInputChange = (field, value) => {
    setBulkData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle product selection and auto-fill price
  const handleProductChange = (productName) => {
    setBulkData(prev => ({
      ...prev,
      productName: productName
    }));

    // Auto-fill price if available
    if (productName) {
      const product = products.find(p => p.name === productName);
      if (product && product.price) {
        setBulkData(prev => ({
          ...prev,
          price: product.price
        }));
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Only include fields that have been changed (not empty)
    const updates = {};
    Object.keys(bulkData).forEach(key => {
      if (bulkData[key] && bulkData[key].toString().trim() !== '') {
        // Convert price to number
        if (key === 'price') {
          updates[key] = parseFloat(bulkData[key]);
        } else {
          updates[key] = bulkData[key];
        }
      }
    });

    // Include emails only for a single selection, and only if they changed
    if (isSingleSelection) {
      ['clientEmail', 'emailEdu'].forEach(key => {
        const newValue = emailData[key].trim();
        if (newValue !== (selectedRecord[key] || '')) {
          updates[key] = newValue;
        }
      });
    }

    if (Object.keys(updates).length === 0) {
      alert('Please select at least one field to update');
      return;
    }

    onBulkUpdate(updates);
  };

  const handleCancel = () => {
    setBulkData({
      paymentMethod: '',
      status: '',
      productName: '',
      price: '',
      subscriptionPeriod: ''
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <Users className="h-6 w-6 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              Bulk Edit {selectedRows.length} Records
            </h2>
          </div>
          <button
            onClick={handleCancel}
            disabled={isUpdating}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
            <p className="text-sm text-blue-700">
              <Check className="h-4 w-4 inline mr-1" />
              Only fields with values will be updated. Leave fields empty to keep existing values.
            </p>
          </div>

          {/* Emails (single selection only) */}
          {isSingleSelection && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Client Email
                </label>
                <input
                  type="email"
                  value={emailData.clientEmail}
                  onChange={(e) => setEmailData(prev => ({ ...prev, clientEmail: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="client@example.com"
                  disabled={isUpdating}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Edu Email
                </label>
                <input
                  type="email"
                  value={emailData.emailEdu}
                  onChange={(e) => setEmailData(prev => ({ ...prev, emailEdu: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="student@university.edu"
                  disabled={isUpdating}
                />
              </div>
            </>
          )}

          {/* Payment Method */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Payment Method
            </label>
            <select
              value={bulkData.paymentMethod}
              onChange={(e) => handleInputChange('paymentMethod', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isUpdating}
            >
              <option value="">Keep existing values</option>
              {paymentMethods.map((method) => (
                <option key={method} value={method}>{method}</option>
              ))}
            </select>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              value={bulkData.status}
              onChange={(e) => handleInputChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isUpdating}
            >
              <option value="">Keep existing values</option>
              {statusOptions.map((status) => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>

          {/* Product Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Product Name
            </label>
            <select
              value={bulkData.productName}
              onChange={(e) => handleProductChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isUpdating}
            >
              <option value="">Keep existing values</option>
              {products.map((product) => (
                <option key={product.id || product.name} value={product.name}>
                  {product.name}
                </option>
              ))}
            </select>
          </div>

          {/* Price */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Price (EGP)
            </label>
            <input
              type="number"
              value={bulkData.price}
              onChange={(e) => handleInputChange('price', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Leave empty to keep existing values"
              disabled={isUpdating}
              min="0"
              step="1"
            />
          </div>

          {/* Subscription Period */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subscription Period
            </label>
            <select
              value={bulkData.subscriptionPeriod}
              onChange={(e) => handleInputChange('subscriptionPeriod', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isUpdating}
            >
              <option value="">Keep existing values</option>
              {subscriptionPeriods.map((period) => (
                <option key={period} value={period}>{period}</option>
              ))}
            </select>
          </div>

          {/* Buttons */}
          <div className="flex items-center justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={handleCancel}
              disabled={isUpdating}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isUpdating}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors disabled:opacity-50"
            >
              {isUpdating ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Update {selectedRows.length} Records
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BulkEditModal;
